<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useApiClient } from "@/api/client";
import { useI18n } from "vue-i18n";
import { useBaseStore } from "@/stores/base";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import type { RentableTypeTargetDO } from "@/api/resources/booking-experts";
import { formatNumber } from "chartjs-graph";
import { saveAs } from "file-saver";
import RentableTypeAssetDO = Modules.BookingExperts.Data.RentableTypeAssetDO;
import BexRentableSegmentDO = Modules.BookingExperts.Data.BexRentableSegmentDO;

const { t } = useI18n();
const store = useBaseStore();
const api = useApiClient("bookingExperts");

const breadcrumbs = [
	{
		title: t("targets.overview.title"),
		disabled: true,
		to: "/targets",
	},
];

// ── Filters ──────────────────────────────────────────────────────────────────

const currentYear = new Date().getFullYear();
const currentWeekNum = getISOWeek(new Date());

const yearOptions = ref<number[]>([]);
const selectedYear = ref<number>(currentYear);
const selectedRentableTypeId = ref<number | null>(null);
const selectedRentableSegmentId = ref<number | null>(null);
// When the user picks a type from a different administration we update the admin
// and restore this value after the triggered reload.
const pendingRentableTypeId = ref<number | null>(null);

const rentableTypeAssets = ref<RentableTypeAssetDO[]>([]);
const rentableSegments = ref<BexRentableSegmentDO[]>([]);

// ── Data ─────────────────────────────────────────────────────────────────────

const allTargets = ref<RentableTypeTargetDO[]>([]);
const mechanicalGapData = ref<Record<string, Record<string, number>>>({});
const loading = ref(false);
const yearsLoading = ref(true);
const assetsLoading = ref(true);
const mechanicalGapLoading = ref(true);
const exportLoading = ref(false);

const rentableTypeOptions = computed(() => {
	const allOption = {
		title: t("targets.overview.filterRentableTypeAll"),
		value: null as number | null,
	};

	let rentableTypes: RentableTypeAssetDO[];
	if (selectedRentableSegmentId.value === null) {
		rentableTypes = rentableTypeAssets.value;
	} else {
		const segment = rentableSegments.value.find(
			(segment) => segment.id === selectedRentableSegmentId.value
		);
		rentableTypes = rentableTypeAssets.value.filter((type) =>
			segment.rentable_type_ids.includes(type.id)
		);
	}

	if (store.bexAdministrations.length <= 1) {
		return [
			allOption,
			...rentableTypes.map((asset) => ({
				title: asset.display_name,
				value: asset.id as number | null,
			})),
		];
	}

	// Multiple administrations: group options under subheader items.
	const grouped = new Map<number, RentableTypeAssetDO[]>();
	for (const asset of rentableTypes) {
		if (asset.administration_id == null) continue;
		if (!grouped.has(asset.administration_id)) {
			grouped.set(asset.administration_id, []);
		}
		grouped.get(asset.administration_id)!.push(asset);
	}

	const items: Array<
		| { title: string; value: number | null }
		| { type: "subheader"; title: string }
	> = [allOption];

	for (const [adminId, types] of grouped) {
		const adminName =
			store.bexAdministrations.find((a) => a.id === adminId)?.name ??
			String(adminId);
		items.push({ type: "subheader", title: adminName });
		items.push(
			...types.map((asset) => ({
				title: asset.display_name,
				value: asset.id as number | null,
			}))
		);
	}

	return items;
});

const rentableSegmentOptions = computed(() => {
	const allOption = {
		title: t("targets.overview.filterRentableSegmentAll"),
		value: null as number | null,
	};

	return [
		allOption,
		...rentableSegments.value.map((s) => ({
			title: s.name,
			value: s.id as number | null,
		})),
	];
});

const loadYears = async () => {
	yearsLoading.value = true;

	const response = await api.getTargetYears({
		administration_id: store.selectedBexAdministration?.id,
	});

	if (response.isOk()) {
		yearOptions.value = response.value;

		if (response.value.length > 0) {
			selectedYear.value = response.value.includes(currentYear)
				? currentYear
				: response.value[0];
			await loadTargets();
		} else {
			// No years → loadTargets (and thus loadMechanicalGapData) never runs;
			// clear the dependent loading flags so the UI leaves the skeleton state.
			loading.value = false;
			mechanicalGapLoading.value = false;
		}
	}

	yearsLoading.value = false;
};

const loadAssets = async () => {
	assetsLoading.value = true;

	// Load all rentable types across all administrations so they can be grouped
	// in the dropdown. The administration_id returned per type drives the grouping.
	const rentableTypesResponse = await api.getRentableTypes();

	if (rentableTypesResponse.isOk()) {
		rentableTypeAssets.value = rentableTypesResponse.value;
	}

	const rentableSegmentsResponse = await api.getRentableSegments();

	if (rentableSegmentsResponse.isOk()) {
		rentableSegments.value = rentableSegmentsResponse.value;
	}

	assetsLoading.value = false;
};

const loadTargets = async () => {
	loading.value = true;
	// Restore a type selection that was pending an administration switch,
	// otherwise reset to All (aggregated).
	selectedRentableTypeId.value = pendingRentableTypeId.value;
	pendingRentableTypeId.value = null;

	const startOfYear = getISOYearStart(selectedYear.value);
	const endOfYear = `${selectedYear.value}-12-31`;

	const [targetsResponse] = await Promise.all([
		api.getTargets({
			"filter[week_between]": `${startOfYear},${endOfYear}`,
			"filter[administration_id]": store.selectedBexAdministration?.id,
			"filter[rentable_type_id]": selectedRentableTypeId.value ?? undefined,
			"filter[segment_id]": selectedRentableSegmentId.value ?? undefined,
		}),
		loadMechanicalGapData(),
	]);

	if (targetsResponse.isOk()) {
		allTargets.value = targetsResponse.value;
	}

	loading.value = false;
};

const loadMechanicalGapData = async () => {
	mechanicalGapLoading.value = true;

	try {
		const response = await api.getTargetProgress({
			year: selectedYear.value,
			administration_id: store.selectedBexAdministration?.id,
			rentable_type_id: selectedRentableTypeId.value ?? undefined,
			segment_id: selectedRentableSegmentId.value ?? undefined,
		});

		if (response.isOk()) {
			mechanicalGapData.value = response.value;
		}
	} finally {
		mechanicalGapLoading.value = false;
	}
};

// Load administrations from the store if not yet populated (e.g. user navigated
// directly to targets without visiting the pricing section first).
const ensureAdministrationsLoaded = async () => {
	if (store.bexAdministrations.length > 0) return;

	const response = await api.getAdministrations();
	if (response.isOk()) {
		store.bexAdministrations = response.value;
		if (!store.selectedBexAdministration && response.value.length > 0) {
			store.selectedBexAdministration = response.value[0];
		}
	}
};

// Reload data whenever the selected administration changes. The { immediate: true }
// guard handles the initial render: if an administration is already selected (shared
// from the pricing section) data loads right away; otherwise we wait for
// ensureAdministrationsLoaded to set it, which triggers this watch again.
watch(
	() => store.selectedBexAdministration?.id,
	async (id) => {
		if (id == null) return;
		await Promise.all([loadYears(), loadAssets()]);
	},
	{ immediate: true }
);

// When the user picks a rentable type (or switches back to All), reload the
// mechanical gap so it reflects the selected filter.
// When multiple administrations exist and the selected type belongs to a
// different administration than the current one, switch the administration
// first; loadTargets will restore the selection after the reload.
watch(selectedRentableTypeId, (id) => {
	if (id !== null) {
		selectedRentableSegmentId.value = null;

		if (store.bexAdministrations.length > 1) {
			const asset = rentableTypeAssets.value.find((a) => a.id === id);
			if (
				asset?.administration_id != null &&
				asset.administration_id !== store.selectedBexAdministration?.id
			) {
				const admin = store.bexAdministrations.find(
					(a) => a.id === asset.administration_id
				);
				if (admin) {
					pendingRentableTypeId.value = id;
					store.selectedBexAdministration = admin;
					return; // the admin watch triggers loadYears + loadTargets which restores the type
				}
			}
		}
	}
	loadMechanicalGapData();
});

watch(selectedRentableSegmentId, (id) => {
	if (id !== null) {
		selectedRentableTypeId.value = null;
	}
	loadTargets();
});

onMounted(async () => await ensureAdministrationsLoaded());

const downloadExcel = async () => {
	exportLoading.value = true;

	const selectedAsset =
		selectedRentableTypeId.value !== null
			? rentableTypeAssets.value.find(
					(a) => a.id === selectedRentableTypeId.value
			  )
			: null;

	const response = await api.exportTargets({
		year: selectedYear.value,
		administration_id:
			selectedAsset?.administration_id ?? store.selectedBexAdministration?.id,
		rentable_type_id: selectedRentableTypeId.value ?? undefined,
		segment_id: selectedRentableSegmentId.value ?? undefined,
	});

	if (response.isOk()) {
		saveAs(response.value, `targets-${selectedYear.value}.xlsx`);
	}

	exportLoading.value = false;
};

// ── Table ─────────────────────────────────────────────────────────────────────

const headers = [
	{ title: t("targets.overview.table.week"), key: "weekNum", sortable: false },
	{
		title: t("targets.overview.table.revYtdLastWeek"),
		key: "revYtdLastWeek",
		align: "end" as const,
		sortable: false,
	},
	{
		title: t("targets.overview.table.revYtd"),
		key: "revYtd",
		align: "end" as const,
		sortable: false,
	},
	{
		title: t("targets.overview.table.revYtdSinceLastWeek"),
		key: "revYtdSinceLastWeek",
		align: "end" as const,
		sortable: false,
	},
	{
		title: t("targets.overview.table.revYtdLastWeekPrevYear"),
		key: "revYtdLastWeekPrevYear",
		align: "end" as const,
		sortable: false,
	},
	{
		title: t("targets.overview.table.revYtdPrevYear"),
		key: "revYtdPrevYear",
		align: "end" as const,
		sortable: false,
	},
	{
		title: t("targets.overview.table.revYtdPrevYearSinceLastWeek"),
		key: "revYtdPrevYearSinceLastWeek",
		align: "end" as const,
		sortable: false,
	},
	{
		title: t("targets.overview.table.target"),
		key: "value",
		align: "end" as const,
		sortable: false,
	},
	{
		title: t("targets.overview.table.gap"),
		key: "gap",
		align: "end" as const,
		sortable: false,
	},
	{
		title: t("targets.overview.table.toCome"),
		key: "toCome",
		align: "end" as const,
		sortable: false,
	},
	{
		title: t("targets.overview.table.mechanicalGap"),
		key: "mechanicalGap",
		align: "end" as const,
		sortable: false,
	},
];

const tableRows = computed(() => {
	const filtered =
		selectedRentableTypeId.value !== null
			? allTargets.value.filter(
					(item) => item.rentable_type_id === selectedRentableTypeId.value
			  )
			: allTargets.value;

	// value is null for weeks that have revenue but no target (columns stay blank).
	const byWeek = new Map<number, { value: number | null; weekKey: string }>();
	for (const target of filtered) {
		const existing = byWeek.get(target.weekNum);
		if (existing) {
			existing.value = (existing.value ?? 0) + parseFloat(target.value);
		} else {
			const weekKey = `${target.year}-${String(target.weekNum).padStart(
				2,
				"0"
			)}`;
			byWeek.set(target.weekNum, {
				value: parseFloat(target.value),
				weekKey,
			});
		}
	}

	// Union in weeks that have revenue data but no target, so their Rev YTD shows.
	for (const weekKey of Object.keys(mechanicalGapData.value)) {
		const weekNum = parseInt(weekKey.slice(-2), 10);
		if (!byWeek.has(weekNum)) {
			byWeek.set(weekNum, { value: null, weekKey });
		}
	}

	return Array.from(byWeek.entries())
		.sort(([a], [b]) => a - b)
		.map(([weekNum, { value, weekKey }]) => {
			const gapRow = mechanicalGapData.value[weekKey] ?? {};
			const revYtdLastWeek = gapRow["Y (LW)"] ?? 0;
			const revYtd = gapRow["Y"] ?? 0;
			const revYtdSinceLastWeek = gapRow["Rev Since Last Week"] ?? 0;
			const revYtdLastWeekPrevYear = gapRow["Y-1 (LW)"] ?? 0;
			const revYtdPrevYear = gapRow["Y-1"] ?? 0;
			const revYtdPrevYearSinceLastWeek =
				gapRow["Rev Y-1 Since Last Week"] ?? 0;
			const toCome = gapRow["To Come"] ?? 0;
			const gap = value === null ? null : revYtd - value;
			const mechanicalGap = gap === null ? null : gap + toCome;
			return {
				weekNum,
				value,
				revYtdLastWeek,
				revYtd,
				revYtdSinceLastWeek,
				revYtdLastWeekPrevYear,
				revYtdPrevYear,
				revYtdPrevYearSinceLastWeek,
				gap,
				toCome,
				mechanicalGap,
			};
		});
});

const yearTotals = computed(() =>
	tableRows.value.reduce(
		(acc, row) => ({
			value: acc.value + (row.value ?? 0),
			revYtdLastWeek: acc.revYtdLastWeek + row.revYtdLastWeek,
			revYtd: acc.revYtd + row.revYtd,
			revYtdSinceLastWeek: acc.revYtdSinceLastWeek + row.revYtdSinceLastWeek,
			revYtdLastWeekPrevYear:
				acc.revYtdLastWeekPrevYear + row.revYtdLastWeekPrevYear,
			revYtdPrevYear: acc.revYtdPrevYear + row.revYtdPrevYear,
			revYtdPrevYearSinceLastWeek:
				acc.revYtdPrevYearSinceLastWeek + row.revYtdPrevYearSinceLastWeek,
			gap: acc.gap + (row.gap ?? 0),
			toCome: acc.toCome + row.toCome,
			mechanicalGap: acc.mechanicalGap + (row.mechanicalGap ?? 0),
		}),
		{
			value: 0,
			revYtdLastWeek: 0,
			revYtd: 0,
			revYtdSinceLastWeek: 0,
			revYtdLastWeekPrevYear: 0,
			revYtdPrevYear: 0,
			revYtdPrevYearSinceLastWeek: 0,
			gap: 0,
			toCome: 0,
			mechanicalGap: 0,
		}
	)
);

// A year can have revenue but no targets
const hasAnyTarget = computed(() =>
	tableRows.value.some((r) => r.value !== null)
);

const kpis = computed(() => {
	const t = yearTotals.value;

	// Gap only counts weeks that have already happened (future weeks have Y=0,
	// which would make the gap artificially negative for the rest of the year).
	const ytdRows =
		selectedYear.value > currentYear
			? []
			: selectedYear.value < currentYear
			? tableRows.value
			: tableRows.value.filter((r) => r.weekNum <= currentWeekNum);

	const ytdGap = ytdRows.reduce((sum, r) => sum + (r.gap ?? 0), 0);
	const mechGap = t.gap + t.toCome;

	const yoyPct = t.revYtdPrevYear
		? ((t.revYtd - t.revYtdPrevYear) / t.revYtdPrevYear) * 100
		: null;
	const achievedPct = t.value ? (t.revYtd / t.value) * 100 : null;
	const mechGapPct = t.value ? (mechGap / t.value) * 100 : null;

	const fmt = (n: number) => (n >= 0 ? "+" : "") + n.toFixed(1) + "%";

	return [
		{
			label: "Rev YTD",
			value: t.revYtd,
			sub: yoyPct != null ? `${fmt(yoyPct)} vs last year` : null,
			subSentiment: yoyPct != null ? ((yoyPct >= 0) as boolean | null) : null,
			icon: "mdi-chart-line",
			color: "primary",
			progress: null as number | null,
		},
		{
			label: "Target",
			value: hasAnyTarget.value ? t.value : (null as number | null),
			sub: achievedPct != null ? `${achievedPct.toFixed(1)}% achieved` : null,
			subSentiment: null as boolean | null,
			icon: "mdi-bullseye-arrow",
			color: "info",
			progress: null as number | null,
		},
		{
			label: "Gap YTD",
			value: hasAnyTarget.value ? ytdGap : (null as number | null),
			sub: null,
			subSentiment: null as boolean | null,
			icon: ytdGap >= 0 ? "mdi-trending-up" : "mdi-trending-down",
			color: ytdGap >= 0 ? "success" : "error",
			progress: null as number | null,
		},
		{
			label: "To Come",
			value: t.toCome,
			sub: null,
			subSentiment: null as boolean | null,
			icon: "mdi-calendar-clock",
			color: "info",
			progress: null as number | null,
		},
		{
			label: "Mech. Gap",
			value: hasAnyTarget.value ? mechGap : (null as number | null),
			sub: mechGapPct != null ? `${fmt(mechGapPct)} of target` : null,
			subSentiment:
				mechGapPct != null ? ((mechGapPct >= 0) as boolean | null) : null,
			icon:
				mechGap >= 0 ? "mdi-check-circle-outline" : "mdi-alert-circle-outline",
			color: mechGap >= 0 ? "success" : "error",
			progress: null as number | null,
		},
	];
});

// ── Helpers ───────────────────────────────────────────────────────────────────

// January 4th is always in ISO week 1, so we find the Monday of that week.
const getISOYearStart = (year: number): string => {
	const jan4 = new Date(year, 0, 4);
	const dayOfWeek = jan4.getDay() || 7; // 1 = Mon … 7 = Sun
	jan4.setDate(jan4.getDate() - (dayOfWeek - 1));
	return jan4.toISOString().slice(0, 10);
};

function getISOWeek(d: Date): number {
	const date = new Date(d);
	date.setHours(0, 0, 0, 0);
	date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
	const week1 = new Date(date.getFullYear(), 0, 4);
	return (
		1 +
		Math.round(
			((date.getTime() - week1.getTime()) / 86400000 -
				3 +
				((week1.getDay() + 6) % 7)) /
				7
		)
	);
}

const isCurrentWeek = (weekNum: number) =>
	selectedYear.value === currentYear && weekNum === currentWeekNum;
</script>

<template>
	<div class="d-flex flex-column pb-6">
		<main-breadcrumbs :breadcrumbs="breadcrumbs" />

		<!-- Toolbar -->
		<div class="d-flex flex-wrap align-center ga-3 mt-5">
			<v-select
				v-if="store.bexAdministrations.length > 1"
				v-model="store.selectedBexAdministration"
				:items="store.bexAdministrations"
				item-title="name"
				return-object
				:label="t('targets.overview.filterAdministration')"
				variant="outlined"
				hide-details
				density="compact"
				style="max-width: 220px"
			/>

			<v-select
				v-model="selectedYear"
				:items="yearOptions"
				:label="t('targets.overview.filterYear')"
				variant="outlined"
				hide-details
				density="compact"
				style="max-width: 140px"
				:loading="yearsLoading"
				@update:model-value="loadTargets"
			/>

			<v-select
				v-model="selectedRentableSegmentId"
				:items="rentableSegmentOptions"
				:label="t('targets.overview.filterRentableSegment')"
				variant="outlined"
				hide-details
				density="compact"
				style="max-width: 220px"
			/>

			<v-select
				v-model="selectedRentableTypeId"
				:items="rentableTypeOptions"
				:label="t('targets.overview.filterRentableType')"
				variant="outlined"
				hide-details
				density="compact"
				style="max-width: 220px"
				:disabled="rentableTypeOptions.length === 0"
			/>

			<v-spacer />

			<v-btn
				variant="tonal"
				color="primary"
				prepend-icon="mdi-download"
				:loading="exportLoading"
				@click="downloadExcel"
			>
				{{ t("targets.overview.exportButton") }}
			</v-btn>

			<v-btn
				variant="flat"
				color="secondary"
				prepend-icon="mdi-upload"
				to="/targets/import"
			>
				{{ t("targets.overview.importButton") }}
			</v-btn>
		</div>

		<!-- KPIs -->
		<div class="kpi-grid mt-3">
			<!-- Loading skeletons -->
			<template v-if="loading || yearsLoading || mechanicalGapLoading">
				<v-card v-for="i in 5" :key="`sk-${i}`">
					<v-card-title class="bg-background" style="height: 48px">
						<v-skeleton-loader type="text" width="90" />
					</v-card-title>
					<div class="d-flex align-center pa-3 ga-3">
						<v-skeleton-loader type="chip" class="flex-grow-1" />
						<v-skeleton-loader type="avatar" />
					</div>
				</v-card>
			</template>

			<!-- Populated cards -->
			<template v-else>
				<v-card v-for="kpi in kpis" :key="kpi.label">
					<v-card-title class="bg-background" style="height: 48px">
						{{ kpi.label }}
					</v-card-title>
					<div class="d-flex align-start pa-3 ga-3" style="flex: 1">
						<div class="flex-grow-1">
							<v-chip
								variant="tonal"
								:color="kpi.color"
								size="large"
								style="font-size: 1rem; font-weight: 500"
							>
								{{
									kpi.value === null
										? "—"
										: formatNumber(kpi.value, "money_no_fraction")
								}}
							</v-chip>
							<div
								v-if="kpi.sub"
								class="text-caption mt-2 ml-1"
								:class="{
									'text-success': kpi.subSentiment === true,
									'text-error': kpi.subSentiment === false,
									'text-medium-emphasis': kpi.subSentiment === null,
								}"
							>
								{{ kpi.sub }}
							</div>
							<v-progress-linear
								v-if="kpi.progress != null"
								:model-value="Math.min(kpi.progress, 100)"
								:color="kpi.color"
								bg-color="surface-variant"
								rounded
								height="4"
								class="mt-3"
							/>
						</div>
						<v-avatar :color="kpi.color" variant="tonal" class="rounded-md">
							<v-icon size="24">{{ kpi.icon }}</v-icon>
						</v-avatar>
					</div>
				</v-card>
			</template>
		</div>

		<!-- Data card -->
		<v-card class="mt-3">
			<v-data-table
				:headers="headers"
				:items="tableRows"
				:loading="loading || yearsLoading || mechanicalGapLoading"
				item-value="weekNum"
				:no-data-text="t('targets.overview.table.noData')"
				:items-per-page="-1"
				hide-default-footer
				density="compact"
				fixed-header
				height="100%"
				:row-props="
					({ item }) =>
						isCurrentWeek(item.weekNum) ? { class: 'current-week' } : {}
				"
			>
				<template #headers>
					<tr>
						<th rowspan="2" class="th-base th-week">
							{{ t("targets.overview.table.week") }}
						</th>
						<th colspan="3" class="th-group th-cy">Current Year</th>
						<th colspan="3" class="th-group th-py">Previous Year</th>
						<th rowspan="2" class="th-base th-right th-section-start">
							Target
						</th>
						<th rowspan="2" class="th-base th-right">Gap</th>
						<th rowspan="2" class="th-base th-right">To Come</th>
						<th rowspan="2" class="th-base th-right">Mech. Gap</th>
					</tr>
					<tr>
						<th class="th-sub th-right">LW</th>
						<th class="th-sub th-right">Now</th>
						<th class="th-sub th-right th-group-end">Since LW</th>
						<th class="th-sub th-right">LW</th>
						<th class="th-sub th-right">Now</th>
						<th class="th-sub th-right th-group-end">Since LW</th>
					</tr>
				</template>

				<template #item.weekNum="{ item }">
					<span :class="{ 'font-weight-bold': isCurrentWeek(item.weekNum) }">
						{{ item.weekNum }}
					</span>
					<v-chip
						v-if="isCurrentWeek(item.weekNum)"
						size="x-small"
						color="primary"
						class="ml-2"
					>
						current
					</v-chip>
				</template>

				<!-- Plain money columns. 'value' (Target) is null for weeks with no
				     target, which renders blank; the other columns are never null. -->
				<template
					v-for="col in [
						'revYtdLastWeek',
						'revYtd',
						'revYtdLastWeekPrevYear',
						'revYtdPrevYear',
						'value',
						'toCome',
					]"
					#[`item.${col}`]="{ item }"
					:key="col"
				>
					<span :class="{ 'font-weight-bold': isCurrentWeek(item.weekNum) }">
						{{
							(item as any)[col] === null
								? "—"
								: formatNumber((item as any)[col], "money_no_fraction")
						}}
					</span>
				</template>

				<!-- Since LW columns — directional colouring + arrow -->
				<template
					v-for="col in ['revYtdSinceLastWeek', 'revYtdPrevYearSinceLastWeek']"
					#[`item.${col}`]="{ item }"
					:key="col"
				>
					<span
						:class="{
						'font-weight-bold': isCurrentWeek(item.weekNum),
						'text-success': (item as any)[col] > 0,
						'text-error': (item as any)[col] < 0,
					}"
					>
						<v-icon v-if="(item as any)[col] > 0" size="12" class="mb-px"
							>mdi-arrow-up</v-icon
						>
						<v-icon v-else-if="(item as any)[col] < 0" size="12" class="mb-px"
							>mdi-arrow-down</v-icon
						>
						{{ formatNumber((item as any)[col], "money_no_fraction") }}
					</span>
				</template>

				<template #item.gap="{ item }">
					<span
						:class="{
							'font-weight-bold': isCurrentWeek(item.weekNum),
							'text-success': item.gap !== null && item.gap >= 0,
							'text-error': item.gap !== null && item.gap < 0,
						}"
					>
						{{
							item.gap === null
								? "—"
								: formatNumber(item.gap, "money_no_fraction")
						}}
					</span>
				</template>

				<template #item.mechanicalGap="{ item }">
					<span
						:class="{
							'font-weight-bold': isCurrentWeek(item.weekNum),
							'text-success':
								item.mechanicalGap !== null && item.mechanicalGap >= 0,
							'text-error':
								item.mechanicalGap !== null && item.mechanicalGap < 0,
						}"
					>
						{{
							item.mechanicalGap === null
								? "—"
								: formatNumber(item.mechanicalGap, "money_no_fraction")
						}}
					</span>
				</template>

				<template #body.append>
					<tr v-if="tableRows.length > 0" class="total-row">
						<td class="font-weight-medium text-medium-emphasis">
							{{ t("targets.overview.table.total") }}
						</td>
						<td class="text-end font-weight-bold">
							{{ formatNumber(yearTotals.revYtdLastWeek, "money_no_fraction") }}
						</td>
						<td class="text-end font-weight-bold">
							{{ formatNumber(yearTotals.revYtd, "money_no_fraction") }}
						</td>
						<td class="text-end font-weight-bold">
							{{
								formatNumber(
									yearTotals.revYtdSinceLastWeek,
									"money_no_fraction"
								)
							}}
						</td>
						<td class="text-end font-weight-bold">
							{{
								formatNumber(
									yearTotals.revYtdLastWeekPrevYear,
									"money_no_fraction"
								)
							}}
						</td>
						<td class="text-end font-weight-bold">
							{{ formatNumber(yearTotals.revYtdPrevYear, "money_no_fraction") }}
						</td>
						<td class="text-end font-weight-bold">
							{{
								formatNumber(
									yearTotals.revYtdPrevYearSinceLastWeek,
									"money_no_fraction"
								)
							}}
						</td>
						<td class="text-end font-weight-bold">
							{{
								hasAnyTarget
									? formatNumber(yearTotals.value, "money_no_fraction")
									: "—"
							}}
						</td>
						<td
							class="text-end font-weight-bold"
							:class="{
								'text-success': hasAnyTarget && yearTotals.gap >= 0,
								'text-error': hasAnyTarget && yearTotals.gap < 0,
							}"
						>
							{{
								hasAnyTarget
									? formatNumber(yearTotals.gap, "money_no_fraction")
									: "—"
							}}
						</td>
						<td class="text-end font-weight-bold">
							{{ formatNumber(yearTotals.toCome, "money_no_fraction") }}
						</td>
						<td
							class="text-end font-weight-bold"
							:class="{
								'text-success': hasAnyTarget && yearTotals.mechanicalGap >= 0,
								'text-error': hasAnyTarget && yearTotals.mechanicalGap < 0,
							}"
						>
							{{
								hasAnyTarget
									? formatNumber(yearTotals.mechanicalGap, "money_no_fraction")
									: "—"
							}}
						</td>
					</tr>
				</template>
			</v-data-table>
		</v-card>
	</div>
</template>

<style scoped lang="scss">
// ── KPI cards ─────────────────────────────────────────────────────────────────

.kpi-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));

	@media (min-width: 960px) {
		grid-template-columns: repeat(5, 1fr);
	}
	gap: 12px;
}

// ── Header ────────────────────────────────────────────────────────────────────

.th-base {
	padding: 0 12px;
	font-size: 0.75rem;
	font-weight: 500;
	white-space: nowrap;
	color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
	border-bottom: 2px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.th-week {
	padding-left: 16px;
}

.th-right {
	text-align: right;
}

.th-group {
	padding: 5px 12px;
	font-size: 0.65rem;
	font-weight: 700;
	letter-spacing: 0.07em;
	text-transform: uppercase;
	text-align: center;
	white-space: nowrap;
	border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.th-cy {
	background: rgba(var(--v-theme-primary), 0.05);
	border-right: 1px solid rgba(var(--v-border-color), 0.15);
}

.th-py {
	background: rgba(var(--v-theme-secondary), 0.05);
	border-right: 2px solid rgba(var(--v-border-color), 0.25);
}

.th-sub {
	padding: 4px 12px 6px;
	font-size: 0.72rem;
	font-weight: 500;
	white-space: nowrap;
	color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
	border-bottom: 2px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.th-group-end {
	border-right: 1px solid rgba(var(--v-border-color), 0.2);
}

.th-section-start {
	border-left: 2px solid rgba(var(--v-border-color), 0.25);
}

// ── Body borders ──────────────────────────────────────────────────────────────

// Column 4 = Since LW (CY), column 7 = Since LW (PY), column 8 = Target
:deep(tbody td:nth-child(4)),
:deep(.total-row td:nth-child(4)) {
	border-right: 1px solid rgba(var(--v-border-color), 0.2);
}

:deep(tbody td:nth-child(7)),
:deep(.total-row td:nth-child(7)) {
	border-right: 1px solid rgba(var(--v-border-color), 0.2);
}

:deep(tbody td:nth-child(8)),
:deep(.total-row td:nth-child(8)) {
	border-left: 2px solid rgba(var(--v-border-color), 0.25);
}

// ── Rows ──────────────────────────────────────────────────────────────────────

:deep(tbody tr:nth-child(even) td) {
	background-color: rgba(var(--v-theme-on-surface), 0.02);
}

.current-week {
	background-color: rgba(var(--v-theme-primary), 0.05);
}

.total-row td {
	border-top: 2px solid rgba(var(--v-border-color), 0.3);
	padding-top: 10px;
	padding-bottom: 10px;
}
</style>
